import { useEffect, useMemo } from "react"
import { Grid } from "@mui/material"
import { debounce, isEqual } from "lodash-es"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { MeetingServiceConfig, MainOrderFormikRef, MeetingServiceTabValues } from "../form/formTypes"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    MeetingServicePriceListState,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { CreateOrderDialogDrawer } from "../drawer/CreateOrderDialogDrawer"
import { DrawerContent } from "../drawer/DrawerContent"
import { CreateOrderType } from "../../helpers"
import { ShopList } from "../shop/ShopList"

const useStyles = makeStyles()(() => ({
    contentSlide: {
        height: "100%",
    },
}))

type MeetingServiceFormContentProps = Readonly<{
    user: SelectedMainUserState
    handleClose: () => void
    dialogType: CreateOrderDialogType
    handleOpenSelectPriceListsDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    isDrawerOpen: boolean
    orderType: CreateOrderType
    meetingServiceConfiguration?: MeetingServiceConfig
    outerFormik: MainOrderFormikRef
    mode: CreateOrderDialogMode
    orderId?: number
}>

export function MeetingServiceFormContent({
    user,
    handleClose,
    dialogType,
    handleOpenSelectPriceListsDialog,
    meetingServiceConfiguration,
    flowConfig,
    isDrawerOpen,
    orderType,
    outerFormik,
    mode,
    orderId,
}: MeetingServiceFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<MeetingServiceTabValues>()
    const priceListState: MeetingServicePriceListState = values["priceList"]
    const { item: priceListItem } = priceListState

    const debouncedSyncValues = useMemo(
        () =>
            debounce((newValues: MeetingServiceTabValues) => {
                Object.entries(newValues).forEach(([key, value]) => {
                    const outerValue = (outerFormik?.current?.values as Record<string, unknown> | undefined)?.[key]
                    // Avoid writing the same value again (can cause reinitialize flicker and query re-fetch loops).
                    if (isEqual(outerValue, value)) return

                    outerFormik?.current?.setFieldValue(key, value)
                })
            }, 400),
        [outerFormik],
    )

    useEffect(() => {
        const outerPriceList = outerFormik.current?.values["priceList"]
        const outerId = outerPriceList?.item?.priceListId
        const localId = priceListItem?.priceListId

        if (outerId && outerId !== localId) {
            setFieldValue("priceList", outerPriceList)
        }
    }, [outerFormik.current?.values["priceList"], priceListItem?.priceListId])

    useEffect(() => {
        debouncedSyncValues(values)
    }, [values, debouncedSyncValues])

    useEffect(() => {
        return () => {
            debouncedSyncValues.cancel()
        }
    }, [debouncedSyncValues])

    return (
        <>
            <Grid container className={classes.contentSlide}>
                {priceListItem && (
                    <ShopList
                        meetingServiceConfiguration={meetingServiceConfiguration}
                        handleCloseMainDialog={handleClose}
                        flowConfig={flowConfig}
                        handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                        dialogType={dialogType}
                        user={user}
                        orderId={orderId}
                    />
                )}
            </Grid>
            {meetingServiceConfiguration && (
                <CreateOrderDialogDrawer open={isDrawerOpen}>
                    <DrawerContent
                        meetingServiceConfiguration={meetingServiceConfiguration}
                        dialogType={dialogType}
                        user={user}
                        orderType={orderType}
                        mode={mode}
                    />
                </CreateOrderDialogDrawer>
            )}
        </>
    )
}
